import React from 'react';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import styles from './ViewCompletedForm.module.scss';
import DescriptionText from 'components/UI/V3/DescriptionText/DescriptionText';
import { PetChip } from 'components/UI/V2/Pets/PetChip/PetChip';
import { PetType } from '@spike/model';
import Spinner from 'components/UI/Spinner';
import type { FormQuestionWithResponse, FormInvitationItemShow, CustomQuestionOption } from '@spike/form-model';
import type { ViewCompletedFormProps, QuestionResponseLite, PetLiteDynamic } from './model';
import { useMasterData } from 'hooks';
import moment from 'moment-timezone';
import store from 'store';
import { ClientEditHeader } from 'components/Client/ClientEditHeader';
import { useMediaQuery } from 'react-responsive';

type AnswerDisplay = string | string[];

const renderAnswer = (answer: string | string[] | null | undefined, keyPrefix: string | number) => {
    if (Array.isArray(answer)) {
        if (!answer.length) return <p className={styles.responseAnswer}>—</p>;

        return (
            <ul className={styles.answerList}>
                {answer.map((item, i) => (
                    <li
                        key={`${keyPrefix}_${i}`}
                        className={styles.answerListItem}
                    >
                        {item}
                    </li>
                ))}
            </ul>
        );
    }

    return <p className={styles.responseAnswer}>{answer || '—'}</p>;
};

const sortActiveOptions = (options: CustomQuestionOption[] = []) =>
    options
        .filter(o => o?.active !== false)
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

const resolveLabelFromOptions = (value: string, optionsSorted: CustomQuestionOption[]): string =>
    optionsSorted.find(o => o.value === value)?.label ?? value;

const orderValuesByOptionPosition = (values: string[], optionsSorted: CustomQuestionOption[]) =>
    values.slice().sort((a, b) => {
        const pa = optionsSorted.find(o => o.value === a)?.position ?? 9999;
        const pb = optionsSorted.find(o => o.value === b)?.position ?? 9999;
        return pa - pb;
    });

const resolveAnswerForDisplay = (q: FormQuestionWithResponse, resp: { answer?: any }): AnswerDisplay => {
    const answerType = q.answerType;
    const optionsSorted = sortActiveOptions(q.options);

    if (answerType === 'multiple_choice') {
        const values = Array.isArray(resp.answer) ? resp.answer : [];
        const orderedValues = orderValuesByOptionPosition(values, optionsSorted);
        return orderedValues.map(v => resolveLabelFromOptions(v, optionsSorted));
    }

    if (answerType === 'single_choice') {
        const v = typeof resp.answer === 'string' ? resp.answer : '';
        return v ? resolveLabelFromOptions(v, optionsSorted) : '';
    }

    return typeof resp.answer === 'string' ? resp.answer : '';
};

// Convert FormQuestionWithResponse[] -> QuestionResponseLite[] for display
const toClientResponses = (questions: FormQuestionWithResponse[] | undefined): QuestionResponseLite[] => {
    if (!Array.isArray(questions)) return [];

    return questions
        .map((q, idx) => {
            const resp = q.response;
            if (!resp) return null;

            const questionText = String(resp.questionSnapshot || q.questionText || '').trim();
            if (!questionText) return null;

            const answer = resolveAnswerForDisplay(q, resp);

            return {
                id: resp.id ?? q.id ?? idx,
                question: questionText,
                answer: answer as any
            } as QuestionResponseLite;
        })
        .filter(Boolean) as QuestionResponseLite[];
};

// Resolve a PetType from incoming value using master data, mirroring PetsResponses.tsx behavior
const resolvePetType = (raw: unknown, allPetTypes: Array<PetType>): PetType | undefined => {
    if (raw && typeof raw === 'object' && 'id' in (raw as any)) return raw as PetType;
    const id = typeof raw === 'string' ? raw : undefined;
    return id ? allPetTypes.find(pt => pt.id === id) : undefined;
};

// Convert pets from FormInvitationItemShow using library model
const toPetsWithResponses = (
    show: FormInvitationItemShow | null | undefined,
    allPetTypes: Array<PetType>
): PetLiteDynamic[] => {
    if (!show || !Array.isArray(show.pets)) return [];
    return show.pets.map(p => {
        const responses = toClientResponses(p.form?.questions);
        const petType = resolvePetType((p as any).petType ?? (p as any).type, allPetTypes);
        return {
            id: typeof p.id === 'string' ? Number(p.id) : (p.id as number),
            name: String(p.name || 'Pet'),
            type: petType as PetType,
            customQuestionResponses: responses
        };
    });
};

export const ViewCompletedForm: React.FC<ViewCompletedFormProps> = ({
    record,
    onBack,
    data,
    loading,
    client,
    onBook,
    onNewSale,
    onClose,
    onSelect,
    hideClientHeader
}) => {
    // Build dynamic normalized structures
    const timeZone = store.getState().login.auth.timeZone!;
    const show = data ?? null;
    const masterData = useMasterData();
    const clientResponses = React.useMemo(() => toClientResponses(show?.customer?.form?.questions), [show]);
    const pets = React.useMemo(() => toPetsWithResponses(show, masterData.petTypes), [show, masterData.petTypes]);
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    const [selectedPetId, setSelectedPetId] = React.useState<number | string | undefined>(() => {
        const firstWithResponses = pets.find(p => p.customQuestionResponses.length > 0);
        return firstWithResponses?.id ?? (pets.length > 0 ? pets[0].id : undefined);
    });

    // Re-evaluate selected pet when pets array changes (after load)
    React.useEffect(() => {
        if (!pets.length) {
            setSelectedPetId(undefined);
            return;
        }
        if (!pets.some(p => p.id === selectedPetId)) {
            const firstWithResponses = pets.find(p => p.customQuestionResponses.length > 0);
            setSelectedPetId(firstWithResponses?.id ?? pets[0].id);
        }
    }, [pets, selectedPetId]);

    const selectedPet = React.useMemo(() => pets.find(p => p.id === selectedPetId), [pets, selectedPetId]);
    const petsWithResponses = React.useMemo(() => pets.filter(p => p.customQuestionResponses.length > 0), [pets]);

    const formTitle = record.form?.title || record.title || record.name || 'Completed Form';
    const rawDate = record.completedAt || record.dateCompleted;
    const filledOn = rawDate ? moment(rawDate).tz(timeZone!).format('MM/DD/YYYY, h:mm a') : '';

    return (
        <>
            {isMobile && onBack && !hideClientHeader ? (
                <div className={styles.editWrapper}>
                    <ClientEditHeader
                        client={client}
                        onSelect={onSelect}
                        onBook={onBook}
                        onNewSale={onNewSale}
                        onClose={onBack}
                    />
                </div>
            ) : (
                <div className={styles.header}>
                    <DefaultHeader
                        title={formTitle}
                        textBelowTitle={filledOn ? `Filled on: ${filledOn}` : undefined}
                        onBack={onBack}
                        displayButton={false}
                        className={styles.headerTitle}
                    />
                </div>
            )}
            <div className={styles.root}>
                {loading && (
                    <div className={styles.loading}>
                        <Spinner />
                    </div>
                )}

                {!loading && (
                    <>
                        {/* Client Responses */}
                        {clientResponses.length > 0 && (
                            <div className={styles.sectionGrid}>
                                <div className={styles.sectionInfo}>
                                    <h4>Client Responses</h4>
                                    <DescriptionText
                                        className={styles.descriptionText}
                                        text="These are the responses the client provided about themselves in the form."
                                    />
                                </div>
                                <div className={styles.responsesContainer}>
                                    {clientResponses.map(r => (
                                        <div
                                            key={r.id}
                                            className={styles.responseCard}
                                        >
                                            <h5 className={styles.responseTitle}>{r.question}</h5>
                                            {renderAnswer(r.answer, r.id)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Pets Responses */}
                        {petsWithResponses.length > 0 && (
                            <div className={styles.sectionGrid}>
                                <div className={styles.sectionInfo}>
                                    <h4>Pets Responses</h4>
                                    <DescriptionText
                                        className={styles.descriptionText}
                                        text="These are the responses the client provided about their pets in the form."
                                    />
                                </div>
                                <div className={styles.petSection}>
                                    <div className={styles.petTabsWrapper}>
                                        {petsWithResponses.map(pet => (
                                            <PetChip
                                                key={pet.id}
                                                petName={pet.name}
                                                petType={pet.type as PetType}
                                                active={pet.id === selectedPetId}
                                                onClick={() => setSelectedPetId(pet.id)}
                                            />
                                        ))}
                                    </div>
                                    <div className={styles.petResponses}>
                                        {selectedPet?.customQuestionResponses.map(r => (
                                            <div
                                                key={r.id}
                                                className={styles.responseCard}
                                            >
                                                <h5 className={styles.responseTitle}>{r.question}</h5>
                                                {renderAnswer(r.answer, r.id)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {clientResponses.length === 0 && petsWithResponses.length === 0 && !loading && (
                            <div className={styles.emptyState}>
                                <DescriptionText text="No responses found for this completed form." />
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default ViewCompletedForm;
